/**
 * Critical Tasks Service
 * Analyzes GMB profile data to detect critical issues and generate tasks
 * Simplified, focused service for task management
 * 
 * @author Visibeen Team - 2024
 */

import logger from '../utils/logger';
import { areCategoriesEquivalent } from '../utils/categoryAnalysis';

/**
 * Critical task definitions with detection logic
 * Priority order matters - first matched tasks shown first
 */
const CRITICAL_TASK_DEFINITIONS = [
    {
        id: 'profile-not-verified',
        title: 'Verify Your Google Business Profile',
        description: 'Your profile is not verified. Complete the verification process to appear on Google Search and Maps.',
        priority: 'Critical',
        icon: '✅',
        category: 'Verification',
        actionType: 'verification',
        actionLabel: 'Start Verification',
        actionUrl: 'https://business.google.com/verifications',
        checkCondition: (gmbData) => {
            // PRIORITY 1: Check VoiceOfMerchant status (most accurate, from VOM API)
            const vomStatus = gmbData?.verificationStatus || gmbData?.voiceOfMerchant?.simplifiedStatus;
            if (vomStatus === 'verified') return false; // Profile IS verified
            if (vomStatus === 'suspended') return false; // Handled by suspension task

            // PRIORITY 2: Check isVerified flag (set by TaskManagementPage)
            if (gmbData?.isVerified === true) return false;

            // PRIORITY 3: Check hasVoiceOfMerchant (indicates verified control)
            if (gmbData?.hasVoiceOfMerchant === true) return false;

            // PRIORITY 4: Check metadata.verified
            if (gmbData?.metadata?.verified === true) return false;

            // PRIORITY 5: Check profile-level verified flag
            if (gmbData?.verified === true || gmbData?.profile?.verified === true) return false;

            // PRIORITY 6: Check verificationState string
            const verificationState = String(
                gmbData?.account?.verificationState ||
                gmbData?.verificationState ||
                gmbData?.profile?.verificationState || ''
            ).toUpperCase();
            if (verificationState === 'VERIFIED') return false;

            // Don't show if suspended (handled by other tasks)
            const suspensionReason = String(gmbData?.metadata?.placeSuspensionReason || '').toUpperCase();
            const isSuspended = suspensionReason.includes('SUSPEND');
            if (isSuspended) return false;

            // Profile is NOT verified - show the task
            return true;
        }
    },
    {
        id: 'profile-suspended',
        title: 'Profile Suspended - Start Reinstatement',
        description: 'Your profile is suspended. Follow Google\'s reinstatement process to restore visibility.',
        priority: 'Critical',
        icon: '🚫',
        category: 'Verification',
        actionType: 'verification',
        actionLabel: 'Request Reinstatement',
        actionUrl: 'https://support.google.com/business/answer/4569145',
        checkCondition: (gmbData) => {
            const suspensionReason = String(gmbData?.metadata?.placeSuspensionReason || '').toUpperCase();
            const metadataState = String(gmbData?.metadata?.state || '').toUpperCase();
            const combined = `${suspensionReason} ${metadataState}`;
            return combined.includes('SUSPEND') && !combined.includes('SOFT');
        }
    },
    {
        id: 'soft-suspension',
        title: 'Profile Restricted - Verify to Restore',
        description: 'Your profile has restrictions. Review and correct your profile details, then re-verify.',
        priority: 'Critical',
        icon: '⚠️',
        category: 'Verification',
        actionType: 'verification',
        actionLabel: 'Fix & Re-Verify',
        actionUrl: 'https://business.google.com/verifications',
        checkCondition: (gmbData) => {
            const suspensionReason = String(gmbData?.metadata?.placeSuspensionReason || '').toUpperCase();
            const metadataState = String(gmbData?.metadata?.state || '').toUpperCase();
            const combined = `${suspensionReason} ${metadataState}`;
            return combined.includes('SOFT') || combined.includes('RESTRICT') || combined.includes('LIMIT');
        }
    },
    {
        id: 'permanently-closed',
        title: 'Reopen Your Business',
        description: 'Your business appears as permanently closed. Request Google to reopen if this is incorrect.',
        priority: 'Critical',
        icon: '🔓',
        category: 'Business Status',
        actionType: 'external',
        actionLabel: 'Request Reopen',
        actionUrl: 'https://business.google.com',
        checkCondition: (gmbData) => {
            const status = String(
                gmbData?.businessStatus ||
                gmbData?.openInfo?.status ||
                gmbData?.profile?.businessStatus || ''
            ).toLowerCase();
            return status.includes('perman') && status.includes('close');
        }
    },
    {
        id: 'temporarily-closed',
        title: 'Reopen Your Business (Temporarily Closed)',
        description: 'Your business is marked as temporarily closed. Add your business hours to reopen it.',
        priority: 'Critical',
        icon: '🔓',
        category: 'Business Status',
        actionType: 'hours',
        actionLabel: 'Add Hours',
        checkCondition: (gmbData) => {
            const status = String(
                gmbData?.businessStatus ||
                gmbData?.openInfo?.status ||
                gmbData?.profile?.businessStatus || ''
            ).toLowerCase();
            return status.includes('tempor') && status.includes('close');
        }
    },
    {
        id: 'map-location',
        title: 'Fix Your Map Location',
        description: 'Your map pin may be incorrect or missing. Update and verify your location on Google Maps.',
        priority: 'Critical',
        icon: '🗺️',
        category: 'Location',
        actionType: 'map',
        actionLabel: 'Fix Location',
        checkCondition: (gmbData) => {
            const lat = gmbData?.latlng?.latitude ?? gmbData?.location?.latlng?.latitude;
            const lng = gmbData?.latlng?.longitude ?? gmbData?.location?.latlng?.longitude;
            const hasLatLng = typeof lat === 'number' && typeof lng === 'number';
            const hasMapsUri = !!gmbData?.metadata?.mapsUri;
            return !hasLatLng || !hasMapsUri;
        }
    },
    {
        id: 'missing-address',
        title: 'Complete Your Address Details',
        description: 'Your address is missing city, pin code, or location name. Add complete address details.',
        priority: 'Critical',
        icon: '🏠',
        category: 'Address',
        actionType: 'address',
        actionLabel: 'Edit Address',
        checkCondition: (gmbData) => {
            const address = gmbData?.storefrontAddress || gmbData?.address || gmbData?.profile?.address || {};
            const locality = String(address?.locality || address?.city || '').trim();
            const postalCode = String(address?.postalCode || address?.zip || '').trim();
            const addressLines = address?.addressLines || address?.lines || [];
            const hasLine = Array.isArray(addressLines)
                ? addressLines.some(l => String(l || '').trim().length > 0)
                : String(addressLines || '').trim().length > 0;

            return !locality || !postalCode || !hasLine;
        }
    },
    {
        id: 'missing-phone',
        title: 'Add Your Phone Number',
        description: 'Your business phone number is missing. Add a contact number or get expert assistance.',
        priority: 'Critical',
        icon: '📞',
        category: 'Contact',
        actionType: 'phone',
        actionLabel: 'Add Phone',
        expertOption: true,
        checkCondition: (gmbData) => {
            return !gmbData?.phoneNumbers?.primaryPhone && !gmbData?.phoneNumber;
        }
    },
    {
        id: 'category-review',
        // Dynamic title and description will be set based on competitor insights
        title: 'Review Your Business Category',
        description: 'Your category may not match competitors in your area. Review and update for better visibility.',
        priority: 'Critical',
        icon: '🏷️',
        category: 'Category',
        actionType: 'category',
        actionLabel: 'Update Category',
        checkCondition: (gmbData) => {
            // Check for competitor insights with category suggestion
            const insights = gmbData?.competitorInsights;
            const suggestedCategory = insights?.suggestedPrimaryCategory ||
                insights?.suggestedCategory ||
                gmbData?.suggestedPrimaryCategory;

            if (!suggestedCategory) return false;

            // Check if explicitly marked as matching
            if (insights?.matchesCompetitors === true) return false;

            const currentCategory = gmbData?.categories?.primaryCategory || gmbData?.primaryCategory;
            const currentName = String(currentCategory?.displayName || currentCategory?.name || currentCategory || '').toLowerCase().trim();
            const suggestedName = String(suggestedCategory?.displayName || suggestedCategory?.name || suggestedCategory || '').toLowerCase().trim();

            // Show task only if categories are truly different (using equivalence check)
            return currentName && suggestedName && !areCategoriesEquivalent(currentName, suggestedName);
        },
        // Dynamic content generator for professional messaging
        getDynamicContent: (gmbData) => {
            const insights = gmbData?.competitorInsights;
            const currentCategory = gmbData?.categories?.primaryCategory || gmbData?.primaryCategory;
            const currentName = String(currentCategory?.displayName || currentCategory?.name || currentCategory || '');
            const suggestedCategory = insights?.suggestedPrimaryCategory ||
                insights?.suggestedCategory ||
                gmbData?.suggestedPrimaryCategory || '';
            const suggestedName = String(suggestedCategory?.displayName || suggestedCategory?.name || suggestedCategory || '');
            const confidence = insights?.confidence || insights?.competitorStats?.topCategoryPercentage || 0;
            const totalCompetitors = insights?.competitorStats?.total || 10;

            // Professional dynamic messaging
            let title = `🔄 Update Category to "${suggestedName}"`;
            let description = '';

            if (confidence >= 70) {
                description = `Your current category "${currentName}" isn't optimal. ${confidence}% of the top ${totalCompetitors} competitors in your area use "${suggestedName}" and rank higher. Switching to this category could significantly improve your Google visibility.`;
            } else if (confidence >= 50) {
                description = `Your category "${currentName}" differs from successful competitors. ${confidence}% of top local businesses use "${suggestedName}". Consider updating to align with industry leaders in your area.`;
            } else {
                description = `We found that "${suggestedName}" is the most common category among your top competitors (${confidence}%). Your current category "${currentName}" may be limiting your visibility.`;
            }

            return {
                title,
                description,
                suggestedCategory: suggestedName,
                currentCategory: currentName,
                confidence,
                competitorStats: insights?.competitorStats
            };
        }
    }
];

/**
 * Default task definitions (shown when no critical tasks exist)
 * These are now dynamic - they calculate progress from GMB data and competitor analysis
 */
const DEFAULT_TASK_TARGET = {
    photos: 5
    // reviews target is now calculated dynamically from competitor data
};

/**
 * Get user's review count from gmbData
 * Also checks competitor data for myBusiness entry
 */
function getUserReviewCount(gmbData) {
    // First try direct review count from gmbData
    let reviewCount = 
        gmbData?.totalReviewCount ||
        gmbData?.reviewCount ||
        gmbData?.reviews?.length ||
        gmbData?.userRatingCount ||
        gmbData?.user_ratings_total ||
        0;
    
    // If no review count, try to get from competitor data (myBusiness entry)
    if (!reviewCount && gmbData?.competitorAnalysis?.competitors_data) {
        let competitorsData = gmbData.competitorAnalysis.competitors_data;
        if (typeof competitorsData === 'string') {
            try {
                competitorsData = JSON.parse(competitorsData);
            } catch (e) { }
        }
        
        const competitors = competitorsData?.competitors || competitorsData || [];
        const myBusiness = Array.isArray(competitors) ? competitors.find(c => c.isMyBusiness === true) : null;
        
        if (myBusiness) {
            reviewCount = getCompetitorReviewCount(myBusiness);
            logger.log('[CriticalTasksService] Got user review count from myBusiness in competitor data:', reviewCount);
        }
    }
    
    return parseInt(reviewCount, 10) || 0;
}

/**
 * Get review count for a competitor
 */
function getCompetitorReviewCount(competitor) {
    const reviewCount = 
        competitor?.totalReviewCount ||
        competitor?.reviewCount ||
        competitor?.reviews ||
        competitor?.userRatingCount ||
        competitor?.user_ratings_total ||
        competitor?.reviewsCount ||
        competitor?.totalReviews ||
        0;
    
    return parseInt(reviewCount, 10) || 0;
}

/**
 * Calculate review goal based on competitor above user (by rank)
 * Uses pre-loaded competitor data from gmbData
 * Returns 25% of the competitor above's review count
 * If competitor above has fewer reviews, skip to next one
 */
function calculateReviewGoalFromCompetitor(gmbData) {
    const defaultResult = { reviewGoal: 0, competitorAbove: null, competitorReviews: 0, userReviewCount: 0 };
    
    logger.log('[CriticalTasksService] calculateReviewGoalFromCompetitor called');
    
    // Debug: dump competitorAnalysis structure
    if (gmbData?.competitorAnalysis) {
        console.log('[CriticalTasksService] FULL competitorAnalysis:', JSON.stringify(gmbData.competitorAnalysis, null, 2).substring(0, 2000));
    }
    
    // Check if competitor data is already in gmbData (from competitor_analyses)
    let competitorsData = gmbData?.competitorAnalysis?.competitors_data || 
                          gmbData?.competitors_data ||
                          gmbData?.competitors ||
                          gmbData?.competitorInsights?.competitors;
    
    logger.log('[CriticalTasksService] competitorsData type:', typeof competitorsData);
    
    if (typeof competitorsData === 'string') {
        try {
            competitorsData = JSON.parse(competitorsData);
            logger.log('[CriticalTasksService] Parsed competitorsData from string');
        } catch (e) {
            logger.warn('[CriticalTasksService] Failed to parse competitorsData:', e.message);
            return defaultResult;
        }
    }
    
    const competitors = competitorsData?.competitors || competitorsData || [];
    
    logger.log('[CriticalTasksService] competitors array length:', Array.isArray(competitors) ? competitors.length : 'not array');
    
    if (!Array.isArray(competitors) || competitors.length === 0) {
        logger.log('[CriticalTasksService] No competitors found, returning default');
        return defaultResult;
    }

    const myReviewCount = getUserReviewCount(gmbData);
    logger.log('[CriticalTasksService] My review count:', myReviewCount);
    
    // Find my business in the list (to get my rank)
    const myBusiness = competitors.find(c => c.isMyBusiness === true);
    const myRank = myBusiness?.rank || gmbData?.my_business_rank || 999;
    
    logger.log('[CriticalTasksService] My business found:', myBusiness ? 'yes' : 'no', 'myRank:', myRank);
    
    // Sort competitors by rank (ascending)
    const sortedCompetitors = competitors
        .filter(c => !c.isMyBusiness)
        .map(c => ({
            ...c,
            rank: parseInt(c.rank, 10) || 999,
            reviewCount: getCompetitorReviewCount(c)
        }))
        .sort((a, b) => a.rank - b.rank);
    
    logger.log('[CriticalTasksService] Sorted competitors (non-myBusiness):', sortedCompetitors.length);
    
    // Find competitors above me
    const competitorsAboveMe = sortedCompetitors.filter(c => c.rank < myRank);
    
    logger.log('[CriticalTasksService] Competitors above me:', competitorsAboveMe.length);
    
    if (competitorsAboveMe.length === 0) {
        logger.log('[CriticalTasksService] No competitors above me, returning default');
        return defaultResult;
    }
    
    // Start from closest competitor above and find one with more reviews
    const competitorsAboveSorted = [...competitorsAboveMe].sort((a, b) => b.rank - a.rank);
    
    let competitorAbove = null;
    
    for (const comp of competitorsAboveSorted) {
        logger.log('[CriticalTasksService] Checking competitor:', comp.name, 'rank:', comp.rank, 'reviews:', comp.reviewCount);
        if (comp.reviewCount > myReviewCount) {
            competitorAbove = comp;
            break;
        }
    }
    
    if (!competitorAbove) {
        logger.log('[CriticalTasksService] No competitor above with more reviews, returning default');
        return defaultResult;
    }
    
    const competitorReviews = competitorAbove.reviewCount;
    
    // Calculate total reviews needed based on formula:
    // Normal: (competitor_reviews - my_reviews) * 50%
    // If competitor has 200%+ more reviews (>= 3x my reviews): my_reviews * 25%
    let totalReviewsNeeded;
    
    // Check if competitor has 200%+ more reviews (i.e., >= 3x my reviews)
    const has200PercentMore = competitorReviews >= (myReviewCount * 3);
    
    if (has200PercentMore || myReviewCount === 0) {
        // Use 25% of my reviews (or minimum based on competitor if I have 0 reviews)
        totalReviewsNeeded = myReviewCount > 0 ? Math.ceil(myReviewCount * 0.25) : Math.ceil(competitorReviews * 0.25);
        logger.log('[CriticalTasksService] Competitor has 200%+ more reviews, total needed:', totalReviewsNeeded);
    } else {
        // Normal case: 50% of the difference
        totalReviewsNeeded = Math.ceil((competitorReviews - myReviewCount) * 0.5);
        logger.log('[CriticalTasksService] Normal case, total needed:', totalReviewsNeeded);
    }
    
    // Cap weekly goal to 2-3 reviews for safe and sustainable growth
    // Min: 2 reviews/week, Max: 3 reviews/week
    const weeklyGoal = Math.min(3, Math.max(2, totalReviewsNeeded));
    
    // Calculate weeks needed to reach the total goal
    const weeksNeeded = Math.ceil(totalReviewsNeeded / weeklyGoal);
    
    logger.log('[CriticalTasksService] Found competitor above:', competitorAbove.name, 
        'reviews:', competitorReviews, 
        'myReviews:', myReviewCount, 
        'totalNeeded:', totalReviewsNeeded,
        'weeklyGoal:', weeklyGoal,
        'weeksNeeded:', weeksNeeded);
    
    return {
        reviewGoal: weeklyGoal,  // Weekly goal (2-3)
        totalReviewsNeeded,      // Total reviews needed to catch up
        weeksNeeded,             // Estimated weeks to reach goal
        competitorAbove,
        competitorReviews,
        userReviewCount: myReviewCount
    };
}

/**
 * Get user's photo count from gmbData
 */
function getUserPhotoCount(gmbData) {
    const media = gmbData?.media || [];
    const photos = media.filter(m => m.mediaFormat === 'PHOTO');
    return photos.length;
}

/**
 * Get photo count for a competitor
 */
function getCompetitorPhotoCount(competitor) {
    const photoCount = 
        competitor?.totalPhotoCount ||
        competitor?.photoCount ||
        competitor?.photos ||
        competitor?.photosCount ||
        competitor?.totalPhotos ||
        competitor?.photo_count ||
        0;
    
    return parseInt(photoCount, 10) || 0;
}

/**
 * Calculate photo goal based on competitor above user (by rank)
 * Uses same formula as reviews:
 * Normal: (competitor_photos - my_photos) * 50%
 * If competitor has 200%+ more: my_photos * 25%
 * Cap weekly goal to 2-3 photos for safe growth
 */
function calculatePhotoGoalFromCompetitor(gmbData) {
    const defaultResult = { photoGoal: 2, totalPhotosNeeded: 0, photoWeeksNeeded: 0, competitorAbovePhoto: null, competitorPhotos: 0, userPhotoCount: 0 };
    
    // Check if competitor data is available
    let competitorsData = gmbData?.competitorAnalysis?.competitors_data || 
                          gmbData?.competitors_data ||
                          gmbData?.competitors ||
                          gmbData?.competitorInsights?.competitors;
    
    if (typeof competitorsData === 'string') {
        try {
            competitorsData = JSON.parse(competitorsData);
        } catch (e) {
            return defaultResult;
        }
    }
    
    const competitors = competitorsData?.competitors || competitorsData || [];
    
    if (!Array.isArray(competitors) || competitors.length === 0) {
        // No competitor data, return default 2 photos/week
        return defaultResult;
    }

    const myPhotoCount = getUserPhotoCount(gmbData);
    
    // Find my business in the list (to get my rank)
    const myBusiness = competitors.find(c => c.isMyBusiness === true);
    const myRank = myBusiness?.rank || gmbData?.my_business_rank || 999;
    
    // Sort competitors by rank (ascending)
    const sortedCompetitors = competitors
        .filter(c => !c.isMyBusiness)
        .map(c => ({
            ...c,
            rank: parseInt(c.rank, 10) || 999,
            photoCount: getCompetitorPhotoCount(c)
        }))
        .sort((a, b) => a.rank - b.rank);
    
    // Find competitors above me
    const competitorsAboveMe = sortedCompetitors.filter(c => c.rank < myRank);
    
    if (competitorsAboveMe.length === 0) {
        return defaultResult;
    }
    
    // Start from closest competitor above and find one with more photos
    const competitorsAboveSorted = [...competitorsAboveMe].sort((a, b) => b.rank - a.rank);
    
    let competitorAbovePhoto = null;
    
    for (const comp of competitorsAboveSorted) {
        if (comp.photoCount > myPhotoCount) {
            competitorAbovePhoto = comp;
            break;
        }
    }
    
    if (!competitorAbovePhoto) {
        // No competitor above with more photos, return default
        return defaultResult;
    }
    
    const competitorPhotos = competitorAbovePhoto.photoCount;
    
    // Calculate total photos needed based on formula
    let totalPhotosNeeded;
    
    // Check if competitor has 200%+ more photos (>= 3x my photos)
    const has200PercentMore = competitorPhotos >= (myPhotoCount * 3);
    
    if (has200PercentMore || myPhotoCount === 0) {
        totalPhotosNeeded = myPhotoCount > 0 ? Math.ceil(myPhotoCount * 0.25) : Math.ceil(competitorPhotos * 0.25);
    } else {
        totalPhotosNeeded = Math.ceil((competitorPhotos - myPhotoCount) * 0.5);
    }
    
    // Cap weekly goal to 2-3 photos for safe and sustainable growth
    const weeklyGoal = Math.min(3, Math.max(2, totalPhotosNeeded));
    
    // Calculate weeks needed
    const weeksNeeded = Math.ceil(totalPhotosNeeded / weeklyGoal);
    
    logger.log('[CriticalTasksService] Photo goal:', {
        myPhotos: myPhotoCount,
        competitorPhotos,
        totalNeeded: totalPhotosNeeded,
        weeklyGoal,
        weeksNeeded
    });
    
    return {
        photoGoal: weeklyGoal,
        totalPhotosNeeded,
        photoWeeksNeeded: weeksNeeded,
        competitorAbovePhoto,
        competitorPhotos,
        userPhotoCount: myPhotoCount
    };
}

/**
 * Generate dynamic default tasks with progress tracking
 * @param {Object} gmbData - GMB profile data
 * @returns {Array} Array of default tasks with progress info
 */
function generateDefaultTasks(gmbData) {
    const tasks = [];

    // ============================================
    // PHOTOS TASK - Based on competitor above's photo count
    // Cap to 2-3 photos per week for safe and sustainable growth
    // ============================================
    const photoResult = calculatePhotoGoalFromCompetitor(gmbData);
    
    const { photoGoal, totalPhotosNeeded, photoWeeksNeeded, competitorAbovePhoto, competitorPhotos, userPhotoCount } = photoResult;

    // Only show photo task if there's a goal
    if (photoGoal > 0) {
        const competitorPhotoName = competitorAbovePhoto?.name || competitorAbovePhoto?.title || 'competitor above you';
        
        tasks.push({
            id: 'add-photos',
            title: `📸 Add ${photoGoal} Photos This Week`,
            description: competitorAbovePhoto 
                ? `Your competitor "${competitorPhotoName}" has ${competitorPhotos} photos while you have ${userPhotoCount}. Add ${photoGoal} photos this week for safe and sustainable growth.`
                : `Businesses with fresh photos get 42% more direction requests. Add ${photoGoal} photos this week.`,
            priority: 'Medium',
            icon: '📸',
            category: 'Engagement',
            actionType: 'photos',
            actionLabel: 'Upload Photos',
            targetCount: photoGoal,
            currentCount: 0,  // Start at 0 - tracking NEW photos to add this week
            remaining: photoGoal,
            progress: 0,
            competitorData: competitorAbovePhoto ? {
                name: competitorPhotoName,
                photos: competitorPhotos,
                userPhotos: userPhotoCount,
                totalPhotosNeeded: totalPhotosNeeded || 0,
                weeksNeeded: photoWeeksNeeded || 0
            } : null
        });
    }

    // ============================================
    // REVIEWS TASK - Based on competitor above's review count (25%)
    // Uses pre-loaded competitor data from gmbData
    // ============================================
    const competitorResult = calculateReviewGoalFromCompetitor(gmbData);
    
    const { reviewGoal, totalReviewsNeeded, weeksNeeded, competitorAbove, competitorReviews, userReviewCount } = competitorResult;

    // Only show review task if there's a competitor above with more reviews
    if (reviewGoal > 0 && competitorAbove) {
        const competitorName = competitorAbove.name || competitorAbove.title || 'competitor above you';
        const competitorRank = competitorAbove.rank || 'N/A';
        
        tasks.push({
            id: 'get-reviews',
            title: `⭐ Get ${reviewGoal} Reviews This Week`,
            description: `Your competitor "${competitorName}" has ${competitorReviews} reviews while you have ${userReviewCount}. Get ${reviewGoal} reviews this week for safe and sustainable growth.`,
            priority: 'Medium',
            icon: '⭐',
            category: 'Engagement',
            actionType: 'reviews',
            actionLabel: 'Share Review Link',
            targetCount: reviewGoal,
            currentCount: 0,  // Start at 0 - this is tracking NEW reviews to get
            remaining: reviewGoal,
            progress: 0,
            competitorData: {
                name: competitorName,
                rank: competitorRank,
                reviews: competitorReviews,
                userReviews: userReviewCount,
                totalReviewsNeeded: totalReviewsNeeded || 0,
                weeksNeeded: weeksNeeded || 0
            }
        });
    }
    // If no competitor above or user has more reviews than all competitors, don't show the task

    return tasks;
}

/**
 * Analyze GMB profile and generate critical tasks
 * @param {Object} gmbData - GMB profile data
 * @returns {Array} Array of critical tasks that apply to this profile
 */
export function analyzeCriticalTasks(gmbData) {
    if (!gmbData) {
        logger.warn('[CriticalTasksService] No GMB data provided');
        return [];
    }

    const criticalTasks = [];

    for (const taskDef of CRITICAL_TASK_DEFINITIONS) {
        try {
            if (taskDef.checkCondition(gmbData)) {
                // Build base task object
                const task = {
                    ...taskDef,
                    generatedAt: new Date().toISOString(),
                    source: 'critical-tasks-service'
                };

                // Apply dynamic content if available (for category task with competitor stats)
                if (typeof taskDef.getDynamicContent === 'function') {
                    try {
                        const dynamicContent = taskDef.getDynamicContent(gmbData);
                        if (dynamicContent) {
                            task.title = dynamicContent.title || task.title;
                            task.description = dynamicContent.description || task.description;
                            task.dynamicData = dynamicContent;
                        }
                    } catch (dynamicError) {
                        logger.warn(`[CriticalTasksService] Error getting dynamic content for ${taskDef.id}:`, dynamicError);
                    }
                }

                criticalTasks.push(task);
                logger.log(`[CriticalTasksService] Critical task detected: ${taskDef.id}`);
            }
        } catch (error) {
            logger.error(`[CriticalTasksService] Error checking task ${taskDef.id}:`, error);
        }
    }

    return criticalTasks;
}

/**
 * Get default engagement tasks (photos/reviews)
 * @param {Object} gmbData - GMB profile data
 * @returns {Array} Array of default tasks
 */
export function getDefaultTasks(gmbData) {
    if (!gmbData) {
        logger.warn('[CriticalTasksService] No GMB data for default tasks');
        // Return static fallback tasks when no data (only photos, reviews require competitor data)
        return [
            {
                id: 'add-photos',
                title: '📸 Add 5 New Photos',
                description: 'Businesses with fresh photos get 42% more direction requests.',
                priority: 'Medium',
                icon: '📸',
                category: 'Engagement',
                actionType: 'photos',
                actionLabel: 'Upload Photos',
                targetCount: 5,
                currentCount: 0,
                remaining: 5,
                progress: 0,
                generatedAt: new Date().toISOString(),
                source: 'default-tasks'
            }
        ];
    }

    // Use dynamic task generator with progress tracking
    const dynamicTasks = generateDefaultTasks(gmbData);

    // Add metadata to each task
    return dynamicTasks.map(task => ({
        ...task,
        generatedAt: new Date().toISOString(),
        source: 'default-tasks'
    }));
}

/**
 * Get all applicable tasks for a profile
 * Critical tasks first, then default tasks if no critical exist
 * @param {Object} gmbData - GMB profile data
 * @returns {Object} { criticalTasks, defaultTasks, hasCriticalTasks }
 */
export function getProfileTasks(gmbData) {
    // Return empty result if no data
    if (!gmbData) {
        return {
            criticalTasks: [],
            defaultTasks: [],
            hasCriticalTasks: false,
            allTasks: []
        };
    }
    
    const criticalTasks = analyzeCriticalTasks(gmbData) || [];
    const hasCriticalTasks = criticalTasks.length > 0;

    // Only show default tasks if no critical tasks
    const defaultTasks = hasCriticalTasks ? [] : (getDefaultTasks(gmbData) || []);

    logger.log(`[CriticalTasksService] Profile analysis complete: ${criticalTasks.length} critical, ${defaultTasks.length} default`);

    return {
        criticalTasks,
        defaultTasks,
        hasCriticalTasks,
        allTasks: [...criticalTasks, ...defaultTasks]
    };
}

/**
 * Generate review link for the profile
 * @param {Object} gmbData - GMB profile data
 * @returns {string} Review link URL
 */
export function generateReviewLink(gmbData) {
    // PRIORITY 1: Use newReviewUri from GMB API (g.page/r/... format - most reliable)
    const newReviewUri = gmbData?.metadata?.newReviewUri || gmbData?.newReviewUri;
    if (newReviewUri) {
        return newReviewUri;
    }

    // PRIORITY 2: Use placeId for writereview link (fallback)
    const placeId = gmbData?.metadata?.placeId || gmbData?.placeId;
    if (placeId) {
        return `https://search.google.com/local/writereview?placeid=${placeId}`;
    }

    // PRIORITY 3: Generic fallback
    return 'https://business.google.com/reviews';
}

/**
 * Get maps URL for the profile
 * @param {Object} gmbData - GMB profile data
 * @returns {string} Google Maps URL
 */
export function getMapsUrl(gmbData) {
    const mapsUri = gmbData?.metadata?.mapsUri;
    if (mapsUri) return mapsUri;

    const placeId = gmbData?.metadata?.placeId || gmbData?.placeId;
    if (placeId) {
        return `https://www.google.com/maps/place/?q=place_id:${placeId}`;
    }

    return 'https://business.google.com';
}

export default {
    analyzeCriticalTasks,
    getDefaultTasks,
    getProfileTasks,
    generateReviewLink,
    getMapsUrl,
    CRITICAL_TASK_DEFINITIONS
};
